import { ClusterTypes } from "@/domain/cluster/constants"
import { getDefaultRelatedCluster } from "@/domain/cluster/getRelatedCluster"
import { NIL_UUID } from "@/domain/resources/constants"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import useGetUserRoles from "@/hooks/useGetUserRoles"
import type {
  ClusterJsonldReadCollectionCluster,
  PeopleJsonldReadItemPeople,
} from "@/networking/api"
import { isNotNullNorUndefined } from "@/utils/ts-utils"
import { createContext, useContext, useMemo, useState } from "react"
import {
  getPeopleStep1Data,
  getPeopleStep2Data,
  people_gen_props,
  peoplecd_gen_props,
} from "../helpers/AddPeopleData"
import type {
  AddPeopleContacDetailsType,
  AddPeopleType,
  EditPeopleContacDetailsType,
  EditPeopleType,
} from "../helpers/AddPeopleData"

type AddOrEditPeopleFormStepContext = {
  formStep1State: AddPeopleType | EditPeopleType
  formStep2State: AddPeopleContacDetailsType | EditPeopleContacDetailsType
  setFormStep1State: React.Dispatch<React.SetStateAction<AddPeopleType | EditPeopleType>>
  setFormStep2State: React.Dispatch<
    React.SetStateAction<AddPeopleContacDetailsType | EditPeopleContacDetailsType>
  >
  clusters?: ClusterJsonldReadCollectionCluster[]
  people?: PeopleJsonldReadItemPeople
  can: {
    create: boolean
    edit: boolean
  }
}

const contextDefaultValue: AddOrEditPeopleFormStepContext = {
  formStep1State: people_gen_props,
  formStep2State: peoplecd_gen_props,
  setFormStep1State: () => {},
  setFormStep2State: () => {},
  can: { create: false, edit: false },
}

const addOrEditPeopleStepFormContext = createContext(contextDefaultValue)

export const AddOrEditPeopleFormStepContext: React.FC<
  React.PropsWithChildren<{ people?: PeopleJsonldReadItemPeople }>
> = ({ people, children }) => {
  const isEdit = isNotNullNorUndefined(people)

  const { hasRoleGuru } = useGetUserRoles()
  const { clusters } = useGetUserInfos()

  const peopleClusterId = useMemo(
    () => (isEdit ? (getDefaultRelatedCluster(people.cluster)?.id ?? NIL_UUID) : NIL_UUID),
    [isEdit, people]
  )

  const { clustersNPDS, clusterNPDSIds } = useMemo(() => {
    const clustersNPDS = clusters?.filter(c =>
      (c.type ?? []).map(t => t.code).includes(ClusterTypes.NPDS)
    )
    const clusterNPDSIds = clustersNPDS.map(c => c.id)
    return { clustersNPDS, clusterNPDSIds }
  }, [clusters])

  const allowCreate = useMemo(
    () => !isEdit && !hasRoleGuru() && clustersNPDS.length > 0,
    [isEdit, hasRoleGuru, clustersNPDS]
  )

  const allowEdit = useMemo(
    () =>
      isEdit &&
      !hasRoleGuru() &&
      clustersNPDS.length > 0 &&
      people.internalSource === "mbv4" &&
      (people.isShared || clusterNPDSIds.includes(peopleClusterId)),
    [isEdit, hasRoleGuru, clustersNPDS, people, clusterNPDSIds, peopleClusterId]
  )

  const can = useMemo(
    () => ({
      edit: allowEdit,
      create: allowCreate,
    }),
    [allowEdit, allowCreate]
  )

  const defaultFormStep1State: AddPeopleType | EditPeopleType = isEdit
    ? getPeopleStep1Data(people)
    : contextDefaultValue.formStep1State
  const defaultFormStep2State: AddPeopleContacDetailsType | EditPeopleContacDetailsType = isEdit
    ? getPeopleStep2Data(people)
    : contextDefaultValue.formStep2State

  const [formStep1State, setFormStep1State] = useState(defaultFormStep1State)
  const [formStep2State, setFormStep2State] = useState(defaultFormStep2State)

  if ((isEdit && !allowEdit) || (!isEdit && !allowCreate)) {
    return null
  }

  return (
    <addOrEditPeopleStepFormContext.Provider
      value={{
        formStep1State,
        formStep2State,
        setFormStep1State,
        setFormStep2State,
        clusters: clustersNPDS,
        can,
      }}
    >
      {children}
    </addOrEditPeopleStepFormContext.Provider>
  )
}

export const useAddOrEditPeopleStepFormContext = () => useContext(addOrEditPeopleStepFormContext)
